/**
 * Shared one-click IDE row used at platform, Codable and project connect.
 * Token is minted by the parent; this only picks the IDE and open/download.
 */
import { Download, ExternalLink, Plug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IDE_CLIENTS, IDE_META, type IdeClient } from '../lib/ide-onboarding';
import { Button } from './ui/Button';

interface Props {
  disabled?: boolean;
  creating?: boolean;
  label: string;
  onLabelChange: (value: string) => void;
  /** One-click: mint if needed, then open this IDE. */
  onConnect: (ide: IdeClient) => void;
  /** Fresh token is already in the session — open/download make sense. */
  canOpenOrDownload: boolean;
  activeIde: IdeClient | null;
  onOpenOnly: () => void;
  onDownloadOnly: () => void;
  /** Project/Codable copy; default is platform-wide. */
  hint?: string;
  title?: string;
}

export function IdeOneClickOnboarding({
  disabled,
  creating,
  label,
  onLabelChange,
  onConnect,
  canOpenOrDownload,
  activeIde,
  onOpenOnly,
  onDownloadOnly,
  hint,
  title,
}: Props) {
  const { t } = useTranslation();
  const busy = !!creating;

  return (
    <div className='border border-blue-500/30 bg-blue-500/5 rounded-lg p-3 space-y-3'>
      {title && (
        <div className='text-sm font-semibold text-blue-100'>{title}</div>
      )}
      <div className='text-xs text-gray-300 leading-relaxed'>
        {hint ??
          t(
            'connect.quickHint',
            'Jedním klikem vytvoří klíč a otevře IDE s předvyplněnou MCP konfigurací. Klíč patří tobě — nemusíš vybírat, jaký LLM v tom IDE běží.',
          )}
      </div>
      <div>
        <label className='block text-xs text-gray-500 mb-1' htmlFor='ide-key-label'>
          {t('connect.labelField', 'Popis (volitelně)')}
        </label>
        <input
          id='ide-key-label'
          className='w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none'
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder={t('connect.labelPlaceholder', 'např. notebook Filip')}
          maxLength={80}
        />
        <p className='mt-1 text-[11px] text-gray-500 leading-relaxed'>
          {t(
            'connect.labelHint',
            'Systém už ví, kdo klíč vydal. Stručný popis je jen pro tebe, ať ho později poznáš.',
          )}
        </p>
      </div>
      <div className='flex flex-wrap gap-2'>
        {IDE_CLIENTS.map((ide) => (
          <Button
            key={ide}
            variant={activeIde === ide ? 'primary' : 'secondary'}
            size='sm'
            icon={<Plug size={14} />}
            loading={busy}
            disabled={disabled || busy}
            onClick={() => onConnect(ide)}
          >
            {t(`connect.quickBtn.${ide}`, IDE_META[ide].label)}
          </Button>
        ))}
      </div>
      <div className='flex flex-wrap items-center gap-2 pt-1 border-t border-blue-500/20'>
        <Button
          variant='ghost'
          size='sm'
          icon={<ExternalLink size={14} />}
          disabled={!canOpenOrDownload || busy}
          onClick={onOpenOnly}
        >
          {t('connect.openOnly', 'Pouze otevřít')}
        </Button>
        <Button
          variant='ghost'
          size='sm'
          icon={<Download size={14} />}
          disabled={!canOpenOrDownload || busy}
          onClick={onDownloadOnly}
        >
          {t('connect.downloadOnly', 'Pouze stáhnout')}
        </Button>
        <span className='text-[10px] text-gray-500'>
          {t(
            'connect.openDownloadHint',
            'Otevřít znovu stejné IDE, nebo stáhnout návod s klíčem a konfigurací.',
          )}
        </span>
      </div>
    </div>
  );
}

export default IdeOneClickOnboarding;
